/**
 * ============================================================
 * VALIDACIONES DE IDENTIDAD – SALVIR
 * ============================================================
 *
 * Este módulo contiene validaciones relacionadas con la
 * identidad de personas dentro del sistema.
 *
 * APLICA PARA:
 *
 * - pacientes
 * - médicos
 * - personal de salud
 *
 
// validaciones curp NOM 024
*/



/**
 * 1️⃣ normalizarCURP
 * Convierte una CURP a mayúsculas y elimina cualquier
 * caracter que no sea letra o número.
 * Se usa para limpiar la entrada del usuario.
 */
export const normalizarCURP = (curp = "") => {
  return curp.toUpperCase().replace(/[^A-Z0-9]/g, "");
};

/**
 * 2️⃣ validarLongitudCURP
 * Verifica que la CURP tenga exactamente 18 caracteres.
 * Esto corresponde a la longitud oficial definida por RENAPO.
 */
export const validarLongitudCURP = (curp) => {
  if (!curp) return false;
  return curp.length === 18;
};

/**
  * 2.5 validarDigitoVerificador
  * Verifica que la posición 18 de la CURP sea un dígito (0-9).
  */
 export const validarDigitoVerificador = (curp) => {
   if (!curp || curp.length < 18) return false;
   const ultimo = curp[17];
   return ultimo >= "0" && ultimo <= "9";
 };

// GENERACION DE UNA CURP GENERICA CON LOS DATOS CORRECTOS PARA COMPARACION
// COMPONENTE RELEVANTE

const PARTICULAS = new Set([
  "DA", "DAS", "DE", "DEL", "DER", "DI", "DIE", "DD",
  "Y", "EL", "LA", "LOS", "LAS", "LE", "LES",
  "MAC", "MC", "VAN", "VON"
]);

export const obtenerPalabraEfectiva = (texto) => {
  const palabras = texto.trim().toUpperCase().split(/\s+/);
  for (let i = 0; i < palabras.length; i++) {
    if (!PARTICULAS.has(palabras[i])) {
      return palabras[i];
    }
  }
  return palabras[0]; // fallback
};

const resolverCaracterCURP = (c) => {
  if (!c) return "X";

  // Excepción explícita del PDF: diéresis en U se conserva como U
  if (c === "Ü") return "U";

  // Regla general del PDF: otras vocales con diéresis van como X
  if (["Ä", "Ë", "Ï", "Ö"].includes(c)) return "X";

  return c;
};

const obtenerVocalInterna = (palabra) => {
  const especiales = new Set(["/", "-", ".", "'", "¨"]);

  for (let i = 1; i < palabra.length; i++) {
    const c = palabra[i];

    if (especiales.has(c)) return "X";

    const resuelto = resolverCaracterCURP(c);

    // Si era vocal con diéresis distinta de Ü, resolverCaracterCURP devuelve X
    if (resuelto === "X" && ["Ä", "Ë", "Ï", "Ö"].includes(c)) return "X";

    // Si era Ü, resolverCaracterCURP devuelve U
    if (/^[AEIOU]$/.test(resuelto)) return resuelto;
  }

  return "X";
};

const limpiarInicial = (letra) => {
  const especiales = new Set(["/", "-", ".", "'", "¨"]);

  if (!letra) return "X";
  if (especiales.has(letra)) return "X";
  if (letra === "Ñ") return "X";

  return resolverCaracterCURP(letra);
};

const NOMBRES_COMUNES = new Set([
   "MARIA", "MA", "MA.", "M", "M.",
   "JOSE", "J", "J."
 ]);

 const obtenerNombreEfectivo = (nombre) => {
  const palabras = nombre.trim().toUpperCase().split(/\s+/);

  if (!palabras.length) return "";

  let i = 0;

  // Si la primera es nombre común o partícula, arrancamos en la segunda
  if (palabras.length > 1 && (NOMBRES_COMUNES.has(palabras[0]) || PARTICULAS.has(palabras[0]))) {
    i = 1;
  }

  let ultimaValida = palabras[i] || palabras[0];

  // Desde ahí, si vienen partículas, las saltamos
  while (i < palabras.length && PARTICULAS.has(palabras[i])) {
    ultimaValida = palabras[i];
    i++;
  }

  return palabras[i] || ultimaValida;
};

 const PALABRAS_INCONVENIENTES = {
  BACA: "BXCA", BAKA: "BXKA", BUEI: "BXEI", BUEY: "BXEY",
  CACA: "CXCA", CACO: "CXCO", CAGA: "CXGA", CAGO: "CXGO",
  CAKA: "CXKA", CAKO: "CXKO", COGE: "CXGE", COGI: "CXGI",
  COJA: "CXJA", COJE: "CXJE", COJI: "CXJI", COJO: "CXJO",
  COLA: "CXLA", CULO: "CXLO", FALO: "FXLO", FETO: "FXTO",
  GETA: "GXTA", GUEI: "GXEI", GUEY: "GXEY", JETA: "JXTA",
  JOTO: "JXTO", KACA: "KXCA", KACO: "KXCO", KAGA: "KXGA",
  KAGO: "KXGO", KAKA: "KXKA", KAKO: "KXKO", KOCE: "KXCE",
  KOGI: "KXGI", KOJA: "KXJA", KOJE: "KXJE", KOJI: "KXJI",
  KOJO: "KXJO", KOLA: "KXLA", KULO: "KXLO", LILO: "LXLO",
  LOCA: "LXCA", LOCO: "LXCO", LOKA: "LXKA", LOKO: "LXKO",
  MAME: "MXME", MAMO: "MXMO", MEAR: "MXAR", MEAS: "MXAS",
  MEON: "MXON", MIAR: "MXAR", MION: "MXON", MOCO: "MXCO",
  MOKO: "MXKO", MULA: "MXLA", MULO: "MXLO", NACA: "NXCA",
  NACO: "NXCO", ORIN: "OXIN", PEDA: "PXDA", PEDO: "PXDO",
  PENE: "PXNE", PIPI: "PXPI", PITO: "PXTO", POPO: "PXPO",
  PUTA: "PXTA", PUTO: "PXTO", QULO: "QXLO", RATA: "RXTA",
  ROBA: "RXBA", ROBE: "RXBE", ROBO: "RXBO", RUIN: "RXIN",
  SENO: "SXNO", TETA: "TXTA", VACA: "VXCA", VAGA: "VXGA",
  VAGO: "VXGO", VAKA: "VXKA", VUEI: "VXEI", VUEY: "VXEY",
  WUEI: "WXEI", WUEY: "WXEY"
};

const obtenerConsonanteInterna = (palabra) => {
   if (!palabra) return "X";
   const especiales = new Set(["/", "-", ".", "'", "¨"]);
   for (let i = 1; i < palabra.length; i++) {
     const c = palabra[i];
     if (especiales.has(c)) return "X";
     if (c === "Ñ") return "X";
     // consonante válida
     if (/^[BCDFGHJKLMNPQRSTVWXYZ]$/.test(c)) {
       return c;
     }
   }
   return "X";
 };
 

export const generarCURP = (nombre, apPat, apMat, fechaNacimiento, sexoCURP, entidadNacimiento, listaEntidades) => {
  
  const palabraApPat = obtenerPalabraEfectiva(apPat);
  const pos1 = limpiarInicial(palabraApPat[0]);
  const pos2 = obtenerVocalInterna(palabraApPat);
  const palabraApMat = obtenerPalabraEfectiva(apMat);
  const pos3 = limpiarInicial(palabraApMat[0]);
  const palabraNombre = obtenerNombreEfectivo(nombre);
  const pos4 = limpiarInicial(palabraNombre[0]);
  const yy = fechaNacimiento.substring(2, 4);
  const mm = fechaNacimiento.substring(5, 7);
  const dd = fechaNacimiento.substring(8, 10);
  const mapaSexoCURP = {
    1: "H",
    2: "M",
    3: "X",
    "1": "H",
    "2": "M",
    "3": "X",
    "Hombre": "H",
    "Mujer": "M",
    "No binario": "X",
  };

  const pos11 = mapaSexoCURP[sexoCURP] || "X";
  const entidadKey = String(entidadNacimiento ?? "").padStart(2, "0");

  const entidad = listaEntidades.find(
    e => String(e.CATALOG_KEY).padStart(2, "0") === entidadKey
  );

  const pos1213 = entidadKey === "88"
    ? "NE"
    : entidad
      ? entidad.ABREVIATURA
      : "NE";
 const baseOriginal = `${pos1}${pos2}${pos3}${pos4}`;
  const baseCorregida = PALABRAS_INCONVENIENTES[baseOriginal] || baseOriginal;

  let base = baseCorregida;
 //   posiciones 14–16
  const pos14 = obtenerConsonanteInterna(palabraApPat);
  const pos15 = obtenerConsonanteInterna(palabraApMat);
  const pos16 = obtenerConsonanteInterna(palabraNombre);

 return `${base}${yy}${mm}${dd}${pos11}${pos1213}${pos14}${pos15}${pos16}`;
};

export const obtenerCasoPalabraInconvenienteCURP = (
  nombre,
  apPat,
  apMat
) => {
  const palabraApPat = obtenerPalabraEfectiva(apPat);
  const pos1 = limpiarInicial(palabraApPat[0]);
  const pos2 = obtenerVocalInterna(palabraApPat);

  const palabraApMat = obtenerPalabraEfectiva(apMat);
  const pos3 = limpiarInicial(palabraApMat[0]);

  const palabraNombre = obtenerNombreEfectivo(nombre);
  const pos4 = limpiarInicial(palabraNombre[0]);

  const baseOriginal = `${pos1}${pos2}${pos3}${pos4}`;
  const baseCorregida = PALABRAS_INCONVENIENTES[baseOriginal];

  if (!baseCorregida) {
    return {
      aplica: false,
      baseOriginal,
      baseCorregida: baseOriginal,
    };
  }

  return {
    aplica: true,
    baseOriginal,
    baseCorregida,
  };
};

/**
 * 3️⃣ validarCURPContraDatos
 * ------------------------------------------------------------
 * Compara la CURP capturada contra una CURP generada a 16
 * dígitos usando los datos del formulario.
 *
 * NO usa regex: genera la CURP esperada con generarCURP()
 * y compara literal posición por posición (1 a 16).
 *
 * Retorna:
 * {
 *   valida: boolean,
 *   errores: [{ posicion: 1-16, esperado: "X", recibido: "Y" }]
 * }
 */
export const validarCURPContraDatos = (curp, nombre, apPat, apMat, fechaNacimiento,  sexoCURP, entidadNacimiento, listaEntidades,  paisNacPaciente) => {

  const resultado = { valida: true, errores: [] };

  if (!curp || curp.length < 16) {
    resultado.valida = false;
    return resultado;
  }

  // CURP genérica de extranjero no se valida
  if (curp === "XXXX999999XXXXXX99") {
    return resultado;
  }

  if (!fechaNacimiento) {
    resultado.valida = false;
    resultado.errores.push({ posicion: 0, motivo: "fecha_requerida" });
    return resultado;
  }

  if (!apMat || !String(apMat).trim()) {
  resultado.valida = false;
  resultado.errores.push({ posicion: 0, motivo: "apellido_materno_requerido" });
  return resultado;
  }

  if (
  paisNacPaciente === undefined ||
  paisNacPaciente === null ||
  String(paisNacPaciente).trim() === ""
) {
  resultado.valida = false;
  resultado.errores.push({ posicion: 0, motivo: "pais_nacimiento_requerido" });
  return resultado;
}

  if (
    entidadNacimiento === undefined ||
    entidadNacimiento === null ||
    String(entidadNacimiento).trim() === ""
  ) {
    resultado.valida = false;
    resultado.errores.push({ posicion: 0, motivo: "entidad_nacimiento_requerida" });
    return resultado;
  }



  const curpGenerada = generarCURP(nombre, apPat, apMat, fechaNacimiento, sexoCURP, entidadNacimiento, listaEntidades);

  if (!curpGenerada || curpGenerada.length < 16) {
    resultado.valida = false;
    return resultado;
  }

  const capturada = curp.toUpperCase();
  const esperada = curpGenerada.toUpperCase();

  const casoPalabraInconveniente = obtenerCasoPalabraInconvenienteCURP(
  nombre,
  apPat,
  apMat
);

const capturadaUsaBaseOriginalInconveniente =
  casoPalabraInconveniente.aplica &&
  capturada.substring(0, 4) === casoPalabraInconveniente.baseOriginal &&
  esperada.substring(0, 4) === casoPalabraInconveniente.baseCorregida;

  for (let i = 0; i < 16; i++) {
  // Posición 11 = sexo registrado ante RENAPO.
  // Se salta SOLO si el char es válido (H/M/X): en ese caso se auto-deriva
  // de la CURP capturada y compararlo sería circular.
  // Si es un char inválido (W, etc.), NO se salta: cae a la comparación
  // de abajo y dispara error de posición 11 (msgCurpPos11).
  if (i === 10 && ["H", "M", "X"].includes(capturada[10])) continue;

  const diferenciaAltisonantePermitida =
    capturadaUsaBaseOriginalInconveniente &&
    i >= 0 &&
    i <= 3 &&
    capturada.substring(0, 4) === casoPalabraInconveniente.baseOriginal &&
    esperada.substring(0, 4) === casoPalabraInconveniente.baseCorregida;

  if (diferenciaAltisonantePermitida) {
    continue;
  }

  if (capturada[i] !== esperada[i]) {
    resultado.valida = false;
    resultado.errores.push({
      posicion: i + 1,
      esperado: esperada[i],
      recibido: capturada[i]
    });
  }
}

if (resultado.valida && capturadaUsaBaseOriginalInconveniente) {
  resultado.requiereConfirmacionPalabraInconveniente = true;
  resultado.palabraInconveniente = {
    baseOriginal: casoPalabraInconveniente.baseOriginal,
    baseCorregida: casoPalabraInconveniente.baseCorregida,
  };
}

const curpAlternativaBaseOriginal = casoPalabraInconveniente.aplica
  ? casoPalabraInconveniente.baseOriginal + esperada.substring(4)
  : null;



if (casoPalabraInconveniente.aplica) {
  console.warn("⚠️ CURP con palabra inconveniente detectada", {
    baseOriginal: casoPalabraInconveniente.baseOriginal,
    baseCorregida: casoPalabraInconveniente.baseCorregida,
    curpCapturada: capturada,
    curpEsperadaCorregida: esperada,
    curpAlternativaBaseOriginal,
    capturadaUsaBaseOriginalInconveniente,
  });
}

  return resultado;
};

/**
 * 4️⃣ extraerFechaDesdeCURP
 * Extrae los componentes de fecha (año, mes, día)
 * de la CURP para poder validarlos o calcular edad.
 */
export const extraerFechaDesdeCURP = (curp) => {
  if (!curp || curp.length < 10) return null;

  const yy = parseInt(curp.substring(4, 6), 10);
  const mm = parseInt(curp.substring(6, 8), 10);
  const dd = parseInt(curp.substring(8, 10), 10);

  return { yy, mm, dd };
};

/**
 * 5️⃣ validarFechaCURP
 * Verifica que la fecha dentro de la CURP sea una fecha real.
 */
export const validarFechaCURP = (curp) => {
  const fecha = extraerFechaDesdeCURP(curp);
  if (!fecha) return false;

  const { yy, mm, dd } = fecha;

  const fechaTest = new Date(2000 + yy, mm - 1, dd);

  if (
    isNaN(fechaTest.getTime()) ||
    fechaTest.getMonth() !== mm - 1 ||
    fechaTest.getDate() !== dd
  ) {
    return false;
  }

  return true;
};


/**
 * 6️⃣ calcularEdadDesdeCURP
 * Calcula la edad de una persona usando la fecha
 * contenida dentro de la CURP.
 *
 * También interpreta el siglo usando la posición 17
 * (homoclave numérica → siglo 1900).
 */
export const calcularEdadDesdeCURP = (curp) => {
  if (!validarFechaCURP(curp)) return null;

  const { yy, mm, dd } = extraerFechaDesdeCURP(curp);

  const sigloChar = (curp[16] || "").toUpperCase();
  const esDigitoSiglo = /^[0-9]$/.test(sigloChar);

  const year = (esDigitoSiglo ? 1900 : 2000) + yy;

  const nacimiento = new Date(year, mm - 1, dd);
  const hoy = new Date();

  let edad = hoy.getFullYear() - nacimiento.getFullYear();

  const m = hoy.getMonth() - nacimiento.getMonth();

  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }

  return edad;
};

export const calcularEdadDesdeFechaNacimiento = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;

  const [year, month, day] = String(fechaNacimiento).split("-").map(Number);

  if (!year || !month || !day) return null;

  const hoy = new Date();

  let edad = hoy.getFullYear() - year;

  const mesActual = hoy.getMonth() + 1;
  const diaActual = hoy.getDate();

  if (
    mesActual < month ||
    (mesActual === month && diaActual < day)
  ) {
    edad--;
  }

  return edad;
};

export const validarFechaNacimientoNoFutura = (fecha) => {
  if (!fecha) return true;

  const fechaNacimiento = new Date(`${fecha}T00:00:00`);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  return fechaNacimiento <= hoy;
};


/**
 * 7️⃣ validarEdadRango
 * Verifica que la edad esté dentro de un rango permitido.
 *
 * Se usa principalmente para validar prestadores
 * (por ejemplo NOM-024 exige edad entre 18 y 90 años).
 */
export const validarEdadRango = (edad, min = 18, max = 90) => {
  if (edad === null || edad === undefined) return false;
  return edad >= min && edad <= max;
};

/**
 * 7.5 validarEdadPaciente
 * Valida que la edad del paciente no exceda 120 años.
 * Si cumple 121 años o más, no se permite registro.
 */
export const validarEdadPaciente = (edad) => {
  if (edad === null || edad === undefined) return false;
  return edad <= 120;
};

 /**
  * 7.6 validarFechaNacimientoMinima
  * Verifica que la fecha de nacimiento no sea anterior a 1900-01-01.
  */
 export const validarFechaNacimientoMinima = (fechaNacimiento) => {
   if (!fechaNacimiento) return true;
   return fechaNacimiento >= "1900-01-01";
 };

/**
 * 8️⃣ coincideCurpConNombre
 * Verifica que la CURP coincida con:
 *
 * - inicial del apellido paterno
 * - inicial del apellido materno
 * - inicial del nombre
 *
 * Esto valida coherencia entre identidad
 * y la clave generada por RENAPO.
 */



/**
 * 9️⃣ esCurpExtranjero
 * Detecta si la CURP corresponde al valor
 * genérico permitido por NOM-024 para personas
 * nacidas fuera de México.
 */
export const esCurpExtranjero = (curp) => {
  return curp === "XXXX999999XXXXXX99";
};

export const entidadNacimientoUsaCurpGenerica = (entidadNacimiento) => {
  if (
    entidadNacimiento === undefined ||
    entidadNacimiento === null ||
    String(entidadNacimiento).trim() === ""
  ) {
    return false;
  }

  const key = String(entidadNacimiento).padStart(2, "0");
  return key === "00" || key === "99";
};

export const paisNacimientoUsaCurpGenerica = (paisNacimiento) => {
  if (
    paisNacimiento === undefined ||
    paisNacimiento === null ||
    String(paisNacimiento).trim() === ""
  ) {
    return false;
  }

  const key = String(paisNacimiento).trim();
  return key === "247" || key === "248";
};

/**
 * 🔟 requiereValidacionCURP
 * Determina si se deben aplicar las validaciones
 * de CURP según el país de nacimiento.
 *
 * NOM-024 indica que solo se valida CURP
 * si el país de nacimiento es México (142).
 */
export const requiereValidacionCURP = (paisNacimiento) => {
  return paisNacimiento === 142;
};

/**
+ *11 validarCurpContraFechaNacimiento
+ * ------------------------------------------------------------
+ * Verifica que la fecha contenida dentro de la CURP
+ * coincida exactamente con la fecha de nacimiento registrada.
+ */
export const validarCurpContraFechaNacimiento = (curp, fechaNacimiento) => {

  if (!curp || !fechaNacimiento) return false;

  const fecha = extraerFechaDesdeCURP(curp);
  if (!fecha) return false;

  const { yy, mm, dd } = fecha;

  const sigloChar = (curp[16] || "").toUpperCase();
  const esDigitoSiglo = /^[0-9]$/.test(sigloChar);

  const year = (esDigitoSiglo ? 1900 : 2000) + yy;

  const fechaCurp =
    year.toString().padStart(4, "0") +
    "-" +
    mm.toString().padStart(2, "0") +
    "-" +
    dd.toString().padStart(2, "0");

  return fechaCurp === fechaNacimiento;
};

export const validarSigloCURPContraFechaNacimiento = (curp, fechaNacimiento) => {
  if (!curp || curp.length < 17 || !fechaNacimiento) return false;

  const year = Number(String(fechaNacimiento).substring(0, 4));
  if (!year) return false;

  const caracter17 = String(curp[16] || "").toUpperCase();

  const esNumero = /^[0-9]$/.test(caracter17);
  const esLetra = /^[A-J]$/.test(caracter17);

  // Nacidos en 1900–1999: posición 17 debe ser número
  if (year >= 1900 && year <= 1999) {
    return esNumero;
  }

  // Nacidos en 2000–2099: posición 17 debe ser letra
  if (year >= 2000 && year <= 2099) {
    return esLetra;
  }

  return false;
};

/**
 * 12 validarEntidadNacimientoCURP
 * ------------------------------------------------------------
 * Verifica que la entidad de nacimiento registrada en el
 * formulario coincida con la entidad codificada dentro de la CURP.
 * La CURP usa la ABREVIATURA de la entidad (ej. DF, NL, AS).
 * Se cruza contra el catálogo ENTIDAD_FEDERATIVA.
 */
export const validarEntidadNacimientoCURP = (curp, entidadNacimiento, listaEntidades) => {

  if (!curp || curp.length !== 18) return true;
  if (!entidadNacimiento) return false;

  const entidadCURP = curp.substring(11, 13).toUpperCase();

  const entidadCatalogo = listaEntidades.find(
    e => e.ABREVIATURA === entidadCURP
  );

  if (!entidadCatalogo) return false;

  return String(entidadCatalogo.CATALOG_KEY) === String(entidadNacimiento);
};

/**
 * 13 validarSexoCURP
 * ------------------------------------------------------------
 * Verifica que el sexo seleccionado en el formulario coincida
 * con el sexo codificado dentro de la CURP (posición 11).
 * Solo se valida cuando la CURP no es genérica.
 */

export const validarSexoCURP = (curp, sexoFormulario) => {
  if (!curp) {
    return true;
  }

  const sexoCURP = curp[10].toUpperCase();

  const mapaSexo = {
    "Hombre": "H",
    "Mujer": "M",
    "Intersexual": "X",
    "No binario": "X"
  };

  return sexoCURP === mapaSexo[sexoFormulario];
};

//ultima validacion, la CURP de ningun paciente puede ser la misma que el prestador....
export const validarCurpPacienteVsPrestador = (curpPaciente, curpPrestador) => {

   if (!curpPaciente) return true;

   // si el prestador no tiene CURP no se valida
   if (!curpPrestador || curpPrestador === "XXXX999999XXXXXX99") {
     return true;
   }

   const paciente = curpPaciente.toUpperCase();
   const prestador = curpPrestador.toUpperCase();

   return paciente !== prestador;
 };

// validacion fecha de nacimiento estimada

export const CURP_GENERICA = "XXXX999999XXXXXX99";

export const construirFechaNacimientoEstimada = (anio, mes = null) => {
  const anioLimpio = String(anio || "").replace(/\D/g, "").slice(0, 4);

  if (anioLimpio.length !== 4) {
    return "";
  }

  // Si hay mes, es menor de 1 año → 15/[mes]/[año]
  if (mes !== null && mes !== "") {
    const mesLimpio = String(mes).replace(/\D/g, "").padStart(2, "0").slice(0, 2);

    if (mesLimpio.length !== 2 || parseInt(mesLimpio, 10) < 1 || parseInt(mesLimpio, 10) > 12) {
      return "";
    }

    return `${anioLimpio}-${mesLimpio}-15`;
  }

  // Sin mes → mayor o igual a 1 año → 30/06/[año]
  return `${anioLimpio}-06-30`;
};

export const obtenerAnioDesdeFechaNacimientoEstimada = (fechaNacimiento) => {
  if (!fechaNacimiento) return "";

  const fecha = String(fechaNacimiento).split("T")[0];
  const [anio, mes, dia] = fecha.split("-");

  // Caso "mayor o igual a 1 año" → 30/06/[año]
  if (mes === "06" && dia === "30") {
    return anio || "";
  }

  // Caso "menor de 1 año" → 15/[mes]/[año]
  if (dia === "15") {
    return anio || "";
  }

  return "";
};

export const obtenerMesDesdeFechaNacimientoEstimada = (fechaNacimiento) => {
  if (!fechaNacimiento) return "";

  const fecha = String(fechaNacimiento).split("T")[0];
  const [, mes, dia] = fecha.split("-");

  // Solo aplica al formato "menor de 1 año" → 15/[mes]/[año]
  if (dia === "15") {
    return mes || "";
  }

  return "";
};

export const esFechaNacimientoEstimadaMenorDeUnAnio = (fechaNacimiento) => {
  if (!fechaNacimiento) return false;

  const fecha = String(fechaNacimiento).split("T")[0];
  const [, , dia] = fecha.split("-");

  return dia === "15";
};

export const prepararPacienteConFechaNacimientoEstimada = (prev) => {
  return {
    ...prev,
    fecha_nacimiento_estimada: true,
    menor_de_un_anio: false,
    fecha_nacimiento: "",
    anio_nacimiento_estimado: "",
    mes_nacimiento_estimado: "",
    curp: CURP_GENERICA,
    sexoCURP: null,
  };
};

export const prepararPacienteSinFechaNacimientoEstimada = (prev) => {
  return {
    ...prev,
    fecha_nacimiento_estimada: false,
    menor_de_un_anio: false,
    fecha_nacimiento: "",
    anio_nacimiento_estimado: "",
    mes_nacimiento_estimado: "",
    curp: "",
    sexoCURP: null,
  };
};

export const aplicarAnioNacimientoEstimadoPaciente = (prev, value) => {
  const anioLimpio = String(value || "").replace(/\D/g, "").slice(0, 4);

  // Si está marcado menor_de_un_anio, usar el mes previamente capturado.
  const mes = prev.menor_de_un_anio ? prev.mes_nacimiento_estimado : null;
  const fechaEstimada = construirFechaNacimientoEstimada(anioLimpio, mes);

  return {
    ...prev,
    anio_nacimiento_estimado: anioLimpio,
    fecha_nacimiento: fechaEstimada,
    curp: CURP_GENERICA,
    sexoCURP: null,
  };
};

export const pacienteTieneFechaNacimientoEstimadaActiva = (perfil, paciente) => {
  return !!(perfil?.nom024 && paciente?.fecha_nacimiento_estimada);
};

export const aplicarMesNacimientoEstimadoPaciente = (prev, value) => {
  const mesLimpio = String(value || "").replace(/\D/g, "").slice(0, 2);
  const fechaEstimada = construirFechaNacimientoEstimada(prev.anio_nacimiento_estimado, mesLimpio);

  return {
    ...prev,
    mes_nacimiento_estimado: mesLimpio,
    fecha_nacimiento: fechaEstimada,
    curp: CURP_GENERICA,
    sexoCURP: null,
  };
};

export const prepararPacienteMenorDeUnAnio = (prev) => {
  return {
    ...prev,
    menor_de_un_anio: true,
    mes_nacimiento_estimado: "",
    // Re-construir fecha: si ya había año capturado, queda inválida hasta que pongan mes.
    fecha_nacimiento: "",
  };
};

export const prepararPacienteNoMenorDeUnAnio = (prev) => {
  // Volver al modo solo año: si ya había año, reconstruir con 30/06.
  const fechaEstimada = construirFechaNacimientoEstimada(prev.anio_nacimiento_estimado);

  return {
    ...prev,
    menor_de_un_anio: false,
    mes_nacimiento_estimado: "",
    fecha_nacimiento: fechaEstimada,
  };
};

export const validarMenorDeUnAnio = (fechaNacimiento) => {
  if (!fechaNacimiento) return false;

  const [year, month, day] = String(fechaNacimiento).split("-").map(Number);
  if (!year || !month || !day) return false;

  const nacimiento = new Date(year, month - 1, day);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const haceUnAnio = new Date(hoy);
  haceUnAnio.setFullYear(haceUnAnio.getFullYear() - 1);

  return nacimiento > haceUnAnio;
};

export const validarMayorOIgualAUnAnio = (fechaNacimiento) => {
  if (!fechaNacimiento) return false;

  const [year, month, day] = String(fechaNacimiento).split("-").map(Number);
  if (!year || !month || !day) return false;

  const nacimiento = new Date(year, month - 1, day);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const haceUnAnio = new Date(hoy);
  haceUnAnio.setFullYear(haceUnAnio.getFullYear() - 1);

  return nacimiento <= haceUnAnio;
};


// =============================
// VALIDACIONES DE IDENTIDAD
// Nombre y apellidos
// =============================
//
// Reglas:
// - mínimo 2 caracteres
// - máximo 50 caracteres
// - solo caracteres permitidos por NOMBRE_REGEX
// =============================

//esta funcion es para quitar
export const quitarAcentos = (str) => {
  return str.split("").map(c => {
    switch(c) {
      case "á": return "a"; case "Á": return "A";
      case "é": return "e"; case "É": return "E";
      case "í": return "i"; case "Í": return "I";
      case "ó": return "o"; case "Ó": return "O";
      case "ú": return "u"; case "Ú": return "U";
      default: return c;
    }
  }).join("");
};


export const validarNombreLongitud = (nombre) => {
  if (!nombre) return false;
  const v = nombre.trim();
  return v.length >= 2 && v.length <= 50;
};

export const validarNombreEspacios = (nombre) => {
  if (!nombre) return false;
  const v = nombre.trim();
  return !/\s{2,}/.test(v);
};

export const validarNombreCaracteres = (nombre) => {
  if (!nombre) return false;  
  const v = nombre.trim();
  const especiales = new Set(["-", ",", ".", "/", "'", "¨"]);
  for (const char of v) {
    if (!/\p{L}/u.test(char) && !especiales.has(char) && char !== " ") return false;
     for (let i = 0; i < v.length - 1; i++) {
    if (especiales.has(v[i]) && especiales.has(v[i + 1])) return false;
  }
  }
  return true;
};

// ============================================================
// VERIFICACIÓN DE PERFIL PRESTADOR
// ============================================================

/**
 * verificarPerfilPrestador
 * ------------------------------------------------------------
 * Verifica que el perfil del médico tenga todos los campos
 * obligatorios para poder dar de alta pacientes.
 *
 * Regresa un array con los campos faltantes.
 * Si el array está vacío, el perfil está completo.
 *
 * NOTA: cuando agregues el switch NOM-024, condicionas
 * esta función a que el switch esté prendido antes de llamarla.
 */
export const verificarPerfilPrestador = (medico, establecimientoCatalogo = null) => {
  const faltantes = [];

  if (!medico?.nombre) faltantes.push("Nombre");
  if (!medico?.apellido_paterno) faltantes.push("Apellido paterno");
  if (!medico?.apellido_materno) faltantes.push("Apellido materno");
  if (!medico?.sexo) faltantes.push("Sexo registrado ante RENAPO");
  if (!medico?.sexo_biologico) faltantes.push("Sexo biológico");
  if (!medico?.fecha_nacimiento) faltantes.push("Fecha de nacimiento");
  if (!medico?.paisNacimiento) faltantes.push("País de nacimiento");
  if (!medico?.curpPrestador) faltantes.push("CURP");
  if (!medico?.perfil?.clues) faltantes.push("CLUES");
  if (!medico?.paisResidencia) faltantes.push("País de residencia");
  if (!medico?.entidadResidencia) faltantes.push("Entidad de residencia");
  if (!medico?.municipio) faltantes.push("Municipio de residencia");
  if (!medico?.localidad) faltantes.push("Localidad de residencia");

  if (![2, 4].includes(Number(medico?.tipoPersonal))) {
    faltantes.push("Tipo de personal");
  }

 if (Number(medico?.tipoPersonal) === 2) {
  if (!String(medico?.perfil?.cedula_profesional || "").trim()) {
    faltantes.push("Cédula profesional");
  }
}

if (Number(medico?.tipoPersonal) === 4) {
  if (!String(medico?.perfil?.cedula_especialidad || "").trim()) {
    faltantes.push("Cédula de especialidad");
  }
}

 const institucionClues = String(
  establecimientoCatalogo?.institucion || ""
).trim().toUpperCase();

const aplicaProgramaSMyMG = ["SSA", "IMB"].includes(institucionClues);

if (
  aplicaProgramaSMyMG &&
  ![0, 1].includes(Number(medico?.programaSMyMG))
) {
  faltantes.push("Programa SMyMG");
}

  return faltantes;
};

